import { DTO, type Nullable } from '@/type-utils';

import { timeStampableToDate } from '@/utils/time-utils';
import { isNullish } from '@/utils/null-utils';
import ISavedAddress from '../Address/ISavedAddress';
import Model from '../Model';
import { ProfileModel } from './Profile';

import type { IAccount } from '.';
import { WalletModel, type IWallet } from '../SavedPayment';

/**
 * Represents a customer's Account: a collection of the customer's
 * saved information, such as payment methods, addresses and personal info.
 */
export default class AccountModel
  extends Model<DTO<IAccount>>
  implements IAccount
{
  /** @inheritdoc */
  public readonly id: string;

  /** @inheritdoc */
  public readonly profile: ProfileModel;

  /** @inheritdoc */
  public readonly wallet: IWallet;

  /** @inheritdoc */
  public readonly savedAddresses: Array<ISavedAddress>;

  /** @inheritdoc */
  public readonly lastLoggedIn: Date;

  /** @inheritdoc */
  public readonly orderHistory: ReadonlyArray<{
    /** The order's unique identifier. */
    orderID: string;

    /** The date the order was placed on. */
    placeDate: Nullable<Date>;
  }>;

  /** @inheritdoc */
  public readonly activeCartID: string | null;

  /** @inheritdoc */
  public constructor(dto: DTO<IAccount>) {
    super(dto);

    this.id = dto.id;
    this.profile = ProfileModel.from(dto.profile);
    this.wallet = dto.wallet;
    this.savedAddresses = [...dto.savedAddresses];
    this.lastLoggedIn = new Date(dto.lastLoggedIn);
    this.activeCartID = dto.activeCartID;

    this.orderHistory = dto.orderHistory.map(({ orderID, placeDate }) => ({
      orderID,
      placeDate: isNullish(placeDate) ? null : timeStampableToDate(placeDate)
    }));
  }

  /**
   * To send a `wallet` back and forth between the API and the client,
   * we need to store the wallet as a DTO of type `IWallet`, as it is a
   * non-class serializable to create a model from it.
   *
   * @returns This getter converts the `wallet` DTO to a `WalletModel` instance,
   * which allows us to access the saved payments as an array of `SavedPaymentModel`
   * instances, instead of a key-value pair.
   */
  public get walletModel(): WalletModel {
    return WalletModel.from(this.wallet);
  }

  /** @inheritdoc */
  public toDTO(): DTO<IAccount> {
    return {
      id: this.id,
      profile: this.profile.toDTO(),
      wallet: this.wallet,
      savedAddresses: this.savedAddresses,
      lastLoggedIn: this.lastLoggedIn.toString(),

      orderHistory: this.orderHistory.map(({ orderID, placeDate }) => ({
        orderID,
        placeDate: isNullish(placeDate) ? null : placeDate.toISOString()
      })),

      activeCartID: this.activeCartID
    };
  }
}
